import { css } from "@emotion/react";
import dayjs from "dayjs";
import { match } from "ts-pattern";

import QuestCard from "~/components/category/[id]/quest/QuestCard";
import { QuestOrder, QuestState } from "~/components/category/[id]/quest/lib";
import { wrapQuest } from "~/components/category/[id]/quest/lib";
import { Props } from "~/components/category/[id]/quest/type";
import { QuestDurationStatus } from "~/domains/quest/lib";
import { mediaQueryIsDesktop, mediaQueryIsMobile } from "~/lib/client/design";
import useUserQuestProgress from "~/lib/client/hooks/quest/useUserQuestProgress";

const QuestListBody = ({
  quests,
  selectedQuestStatus,
  selectedSort,
  selectedDurationStatus,
  selectedQuestGenreTags,
}: {
  quests: Props["category"]["quests"];
  selectedQuestStatus: QuestState[];
  selectedSort: QuestOrder;
  selectedDurationStatus: QuestDurationStatus;
  selectedQuestGenreTags: Props["category"]["quests"][number]["questGenreTags"];
}) => {
  const questProgress = useUserQuestProgress(quests);

  const wrappedQuests = quests.map((quest) =>
    wrapQuest(
      quest,
      questProgress.currentUserQuestProgresses,
      questProgress.currentUserQuestTaskProgresses
    )
  );

  const appliedQuests = wrappedQuests
    .filter(
      (
        quest // 期間に関するフィルター
      ) =>
        match(selectedDurationStatus)
          .with("OPENING", () => quest.isOpened)
          .with("BEFORE_PERIOD", () => quest.isNotOpened)
          .with("AFTER_PERIOD", () => quest.isClosed)
          .exhaustive()
    )
    .filter((quest) => {
      // ユーザのクエスト進行度ステータスに関するフィルター
      if (selectedQuestStatus.length == 0) {
        return true;
      }

      const status = quest.progress.status;
      return selectedQuestStatus.some((s) => s.status === status);
    })
    .filter((quest) => {
      // クエストのジャンルタグに関するフィルター
      if (selectedQuestGenreTags.length == 0) {
        return true;
      }

      return selectedQuestGenreTags.some((tag) =>
        quest.questGenreTags.some((questTag) => questTag.id === tag.id)
      );
    })
    .sort((a, b) =>
      match(selectedSort.value)
        .with("start-at", () => dayjs(a.startAt).diff(dayjs(b.startAt)))
        .with("end-at", () => dayjs(a.endAt).diff(dayjs(b.endAt)))
        .with("point", () => b.point.pointValue - a.point.pointValue)
        .exhaustive()
    );

  return (
    <div css={rootStyle}>
      {appliedQuests.map((quest, index) => (
        <QuestCard quest={quest} key={index} />
      ))}
    </div>
  );
};

const rootStyle = css`
  ${mediaQueryIsMobile(css`
    display: flex;
    flex-direction: column;
    gap: 1rem;
  `)}

  ${mediaQueryIsDesktop(css`
    display: grid;
    grid-template-columns: repeat(auto-fill, 30rem);

    justify-content: space-evenly;
    gap: 2rem;
  `)}
`;

export default QuestListBody;
